// The tasting flow's realistic bottle (the design board's `u-bottle` and `u-bottle-blind`): glass
// shading, a foil capsule and a printed label, 40x120 like the flat Bottle. The wine's hue comes from
// the palette tokens; glass, foil and paper are physical colours and stay fixed in every theme.
import "./config";
import { Svg, paint } from "./Graphic";
import type { PaletteKey } from "./palette";
import { useSvgId } from "./svgId";

export interface BottleArtProps {
  /** druif's colour vocabulary: white, rose, red, sparkling or fortified; anything else reads as neutral. */
  colour: string;
  /** A mystery bottle in a dark wrap with a crossed-out mark: no label, and the wine's colour stays hidden. */
  blind?: boolean;
  /** Rendered width in px; the height follows the 40x120 drawing. */
  width?: number;
  /** Announces the bottle as an image; without it the drawing is decorative. */
  label?: string | undefined;
}

const WINE_TONE: ReadonlyMap<string, PaletteKey> = new Map([
  ["white", "wineWhite"],
  ["rose", "wineRose"],
  ["red", "wineRed"],
  ["sparkling", "wineSparkling"],
  ["fortified", "wineFortified"],
]);

/** The palette token for a wine colour; a colour outside the vocabulary reads as a neutral grey. */
export function wineTone(colour: string): PaletteKey {
  return WINE_TONE.get(colour) ?? "textFaint";
}

const BODY = "M14 8v18c0 6-10 10-10 22v62a6 6 0 0 0 6 6h20a6 6 0 0 0 6-6V48c0-12-10-16-10-22V8z";
const CAPSULE = "M13 2h14v10c0 3-1 5-2 6H15c-1-1-2-3-2-6z";

export function BottleArt({
  colour,
  blind = false,
  width = 40,
  label,
}: BottleArtProps): React.JSX.Element {
  const id = useSvgId();
  return (
    <Svg viewBox="0 0 40 120" width={width} height={width * 3} label={label}>
      <defs>
        <radialGradient id={`${id}-shadow`} cx=".5" cy=".5" r=".5">
          <stop offset="0" stopColor="#000" stopOpacity=".42" />
          <stop offset="1" stopColor="#000" stopOpacity="0" />
        </radialGradient>
        <linearGradient id={`${id}-glass`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#000" stopOpacity=".35" />
          <stop offset=".2" stopColor="#fff" stopOpacity=".22" />
          <stop offset=".45" stopColor="#000" stopOpacity="0" />
          <stop offset=".8" stopColor="#000" stopOpacity=".25" />
          <stop offset="1" stopColor="#000" stopOpacity=".5" />
        </linearGradient>
        {blind ? null : (
          <>
            <linearGradient id={`${id}-foil`} x1="0" y1="0" x2="1" y2="0">
              <stop offset="0" stopColor="#5a0f29" />
              <stop offset=".4" stopColor="#a8405f" />
              <stop offset="1" stopColor="#4a0a20" />
            </linearGradient>
            <linearGradient id={`${id}-paper`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor="#fffdf6" />
              <stop offset="1" stopColor="#efe6d2" />
            </linearGradient>
          </>
        )}
      </defs>
      <ellipse cx="20" cy="117" rx="12" ry="2.4" fill={`url(#${id}-shadow)`} />
      {blind ? (
        <path d={BODY} fill="#3b2a34" />
      ) : (
        <path data-part="wine" d={BODY} fill={paint(wineTone(colour))} />
      )}
      <path d={BODY} fill={`url(#${id}-glass)`} />
      {blind ? (
        <>
          <path d={CAPSULE} fill="#2a2a2a" />
          <g data-part="blind-mark">
            <path d="M4 56l32 6v10L4 66z" fill="#2b2b2b" />
            <path d="M4 66l32 6v10L4 76z" fill="#1d1d1d" />
            <path d="M4 76l32 6v6L4 82z" fill="#2b2b2b" />
            <circle cx="20" cy="70" r="5" fill="none" stroke="#9a9a9a" strokeWidth="1.2" />
            <path d="M16 66l8 8" stroke="#9a9a9a" strokeWidth="1.2" />
          </g>
        </>
      ) : (
        <>
          <path d={CAPSULE} fill={`url(#${id}-foil)`} />
          <rect x="13" y="2" width="14" height="2.5" fill="#e4b556" />
          <g data-part="label">
            <rect x="8" y="60" width="24" height="30" rx="1.5" fill={`url(#${id}-paper)`} />
            <rect
              x="8"
              y="60"
              width="24"
              height="30"
              rx="1.5"
              fill="none"
              stroke="#c9b48f"
              strokeWidth=".6"
            />
            <path d="M11 70h18M12 74h16M13 78h14" stroke="#7b1e3a" strokeWidth="1" opacity=".55" />
            <path d="M20 63l2 2-2 2-2-2z" fill="#7b1e3a" />
            <rect x="11" y="83" width="18" height="2.4" fill="#2a1119" opacity=".55" />
          </g>
        </>
      )}
      <path
        d="M7.5 50v58"
        stroke="#fff"
        strokeWidth="1.6"
        opacity={blind ? 0.25 : 0.35}
        strokeLinecap="round"
      />
    </Svg>
  );
}
